<template>
    <UIGridContainer>
        <SeoStructuredDataHowto :data="howTo"></SeoStructuredDataHowto>
        <UIGridRow>
            <UIGridCol cols="12" md="8" offset-md="2">
                <h1>{{ howTo.title }}</h1>
                <div class="intro" v-html="howTo.description"></div>
                <UIGridRow class="steps">
                    <UIGridCol v-for="(step, index) in howTo.steps" :key="index" cols="12" md="4" class="step">
                        <template v-if="!step.text">
                            <span>{{ index + 1 }}.</span>
                            <span v-html="step"></span>
                        </template>
                        <template v-else>
                            <UILightboxGallery
                                resource
                                class="image mb-2 mb-md-3 d-block border"
                                :thumbnails="[step.image]"
                                :large-images="[step.image]"
                                :count="false"
                                :buttons="false"
                            />
                            <span>{{ index + 1 }}.</span>
                            <span v-html="step.text"></span>
                        </template>
                    </UIGridCol>
                </UIGridRow>
                <div v-if="howTo.content" class="content" v-html="howTo.content"></div>
                <SocialShare
                    v-if="$locale.isTarget()"
                    class="social pt-3"
                    :item="howTo"
                    :label="'Share this article'"
                />
                <PopularBrandHorizontal v-if="totalStock > 0" class="mt-5" :brand="brand" />
            </UIGridCol>
        </UIGridRow>
    </UIGridContainer>
</template>

<script>
import { UIGridContainer, UIGridRow, UIGridCol, UILightboxGallery } from '@dundle/ui/components';
import { SocialShare } from '~/components/blog';
import PopularBrandHorizontal from '~/components/popular/PopularBrandHorizontal';

import FaqData from '~/assets/faq';
import SeoStructuredDataHowto from '~/components/seo/SeoStructuredDataHowto';
import TranslationMixin from '~/mixins/TranslationMixin';
import BreadcrumbMixin from '~/mixins/BreadcrumbMixin';

export default {
    components: {
        SeoStructuredDataHowto,
        PopularBrandHorizontal,
        UIGridRow,
        UIGridCol,
        UIGridContainer,
        SocialShare,
        UILightboxGallery,
    },

    mixins: [TranslationMixin, BreadcrumbMixin],

    async asyncData({ app, params, error, query, store }) {
        try {
            const brand = await app.$api.brand.getBySlug(params.brand);
            const FAQ = FaqData.find(item => item.brandId === params.brand && item.language === app.$locale.language); // REPLACE THIS WITH FAQ API CALL
            const howTo = FAQ.howTo.find(item => item.slug === params.howto);

            if (howTo) {
                return {
                    brand,
                    howTo,
                };
            } else {
                error({
                    statusCode: 404,
                    message: 'Question not found',
                });
            }
        } catch (e) {
            console.error(e);
            error({
                statusCode: 404,
                message: 'Brand not found',
            });
        }
    },

    head() {
        return {
            title: this.translateParams(this.howTo.metaTitle),
            meta: [
                {
                    hid: 'description',
                    name: 'description',
                    content: this.translateParams(this.howTo.metaDescription),
                },
                {
                    hid: 'robots',
                    name: 'robots',
                    content: this.$locale.isTarget() ? 'index, follow' : 'noindex, nofollow',
                },
            ],
        };
    },
    computed: {
        totalStock() {
            return this.brand.products.map(product => product.stock).reduce((a, b) => a + b);
        },
    },
    mounted() {
        this.$gtm?.push({ event: `page_view_brand_faq_howto`, value: this.brand.slug });
    },

    meta: {
        footer: 'version-3',
    },

    breadcrumb({ app }, data) {
        return [
            {
                name: app.$locale.regio.toLowerCase(),
                link: app.$locale.path('index'),
                url: 'https://' + app.$locale.domain + app.$locale.path('index'),
            },
            {
                name: app.$category.getName(data.brand.category),
                link: app.$category.getPath(data.brand.category),
                url: 'https://' + app.$locale.domain + app.$category.getPath(data.brand.category),
            },
            {
                name: app.$brand.getName(data.brand),
                link: app.$brand.getPath(data.brand),
                url: 'https://' + app.$locale.domain + app.$brand.getPath(data.brand),
            },
            {
                // TODO
                name: 'FAQ',
                link: app.$locale.path({ name: 'brand-faq', params: { brand: data.brand.slug } }),
                url:
                    'https://' +
                    app.$locale.domain +
                    app.$locale.path({ name: 'brand-faq', params: { brand: data.brand.slug } }),
            },
            {
                name: data.howTo.title,
                url:
                    'https://' +
                    app.$locale.domain +
                    app.$locale.path({ name: 'brand-faq', params: { brand: data.brand.slug } }) +
                    '/' +
                    data.howTo.slug,
            },
        ];
    },
};
</script>

<style lang="scss" scoped>
.social {
    border-top: 1px solid $color-grey-3;
    border-bottom: 1px solid $color-grey-3;
    margin-top: 5rem;

    ::v-deep .networks {
        margin-bottom: 1rem;
    }
}

.step {
    margin-bottom: 2rem;
}

.image {
    ::v-deep .gallery {
        .img-wrapper {
            margin: 0;
            width: 100%;

            &:first-child {
                margin-left: 0 !important;
            }

            img {
                width: 100%;
                height: auto;
            }
        }

        .thumbnails {
            border: none;
            background: transparent;
            padding: 0;
            margin: 0;
        }
    }
}
</style>
